import React, { useEffect, useState, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  formatAcademicYear,
} from "../utils/academicYear";
import {
  getGradeLevelFromProgressionKey,
  getProgressionTargetLabel,
  parseClassroomHandoverSelection,
} from "../utils/classroomHandoverSelection";
import useAcademicYearOptions from "../utils/useAcademicYearOptions";
import "./ClassFilter.css";

function ClassFilter({
  classes,
  setFilteredClasses,
  gradeLevelOptions,
  teacherRoles,
  showCurrentYearOnly = false,
  schoolid,
  handoverSelection,
  setSelectedGradeForBanner,
  asOfDate,
}) {
  const location = useLocation();

  const academicYears = useAcademicYearOptions({
    asOfDate,
    previousYears: showCurrentYearOnly ? 0 : 2,
  });
  const requestedSelection = useMemo(() => {
    if (handoverSelection !== undefined) return handoverSelection;
    return parseClassroomHandoverSelection(location.search);
  }, [handoverSelection, location.search]);
  const requestedAcademicYear = requestedSelection?.academicYear;
  const requestedProgressionKey = requestedSelection?.progressionKey;
  const requestedProgressionLabel = getProgressionTargetLabel(
    requestedSelection?.progressionPoint?.label
  );
  const requestedYear = academicYears.find((year) => year.storage === requestedAcademicYear);

  const defaultGradeLevel = useMemo(() => {
    if (!teacherRoles) return "all";

    const lowerCasedRoles = teacherRoles.map((role) => role.toLowerCase());

    if (
      lowerCasedRoles.some((role) => role.includes("set")) ||
      lowerCasedRoles.some((role) => role.includes("senco"))
    ) {
      return "all";
    }
    else if (lowerCasedRoles.some((role) => role.includes("junior infant"))) {
      return "grade1";
    }
    else if (lowerCasedRoles.some((role) => role.includes("senior infant"))) {
      return "grade2";
    }
    else if (lowerCasedRoles.some((role) => role.includes("1st class"))) {
      return "grade3";
    }
    else {
      return "all";
    }
  }, [teacherRoles]);

  // Use the current academic year as default
  const [selectedFilter, setSelectedFilter] = useState(
    requestedYear?.display || academicYears[0]?.display || "all"
  );
  const [selectedGradeLevel, setSelectedGradeLevel] = useState(
    requestedProgressionKey || defaultGradeLevel
  );
  const [selectedProgressionKey, setSelectedProgressionKey] = useState(
    requestedProgressionKey || defaultGradeLevel
  );
  const previousCurrentYear = useRef(academicYears[0]?.display);

  // Update selectedGradeLevel when defaultGradeLevel changes
  useEffect(() => {
    if (!requestedProgressionKey) {
      setSelectedGradeLevel(defaultGradeLevel);
      setSelectedProgressionKey(defaultGradeLevel);
    }
  }, [defaultGradeLevel, requestedProgressionKey]);

  useEffect(() => {
    if (requestedYear) setSelectedFilter(requestedYear.display);
    if (requestedProgressionKey) {
      setSelectedGradeLevel(requestedProgressionKey);
      setSelectedProgressionKey(requestedProgressionKey);
    }
  }, [requestedProgressionKey, requestedYear]);

  // Communicate selected grade level back to parent for banner updates
  useEffect(() => {
    if (setSelectedGradeForBanner) {
      setSelectedGradeForBanner(selectedGradeLevel);
    }
  }, [selectedGradeLevel, setSelectedGradeForBanner]);

  useEffect(() => {
    const currentYear = academicYears[0]?.display;
    if (!currentYear) return;

    if (showCurrentYearOnly || selectedFilter === previousCurrentYear.current) {
      setSelectedFilter(currentYear);
    }
    previousCurrentYear.current = currentYear;
  }, [showCurrentYearOnly, academicYears, selectedFilter]);

  useEffect(() => {
    let filteredClasses = classes;

    if (selectedProgressionKey !== "all") {
      const gradeLevel = getGradeLevelFromProgressionKey(
        selectedProgressionKey
      );
      filteredClasses = filteredClasses.filter((item) => {
        if (gradeLevel !== null) {
          return item[`grade_level${gradeLevel}`] === true;
        }
        return item.progressionKey === selectedProgressionKey || item.progression_key === selectedProgressionKey;
      });
    }

    // Apply the selected view filter
    if (selectedFilter !== "all") {
      const yearFilter = academicYears.find(year => year.display === selectedFilter);

      if (yearFilter) {
        filteredClasses = filteredClasses.filter((item) => {
          const itemYear = item.academicYear || item.academic_year || item.year;
          if (!itemYear) return false;

          try {
            return formatAcademicYear(itemYear, "storage") === yearFilter.storage;
          } catch (error) {
            console.warn("Ignoring class with invalid academic year:", itemYear);
            return false;
          }
        });
      }
    }

    // Sort classes so that those needing to "Assign New Classroom teacher" come first
    filteredClasses.sort((a, b) => {
      const aNeedsAssign = a.name.includes("Assign New Classroom teacher");
      const bNeedsAssign = b.name.includes("Assign New Classroom teacher");
      return bNeedsAssign - aNeedsAssign;
    });

    setFilteredClasses(filteredClasses);
  }, [
    selectedFilter,
    selectedProgressionKey,
    classes,
    setFilteredClasses,
    academicYears
  ]);

  const configuredGradeLevelOptions = gradeLevelOptions
    ? Object.keys(gradeLevelOptions[0] || {})
      .filter((key) => getGradeLevelFromProgressionKey(key) !== null)
      .sort((first, second) =>
        getGradeLevelFromProgressionKey(first) -
        getGradeLevelFromProgressionKey(second)
      )
    : [];
  const hasCustomRequestedProgression = Boolean(
    requestedProgressionKey &&
    !configuredGradeLevelOptions.includes(requestedProgressionKey)
  );
  const matchingConfiguredKey = hasCustomRequestedProgression && requestedProgressionLabel
    ? configuredGradeLevelOptions.find((key) =>
      gradeLevelOptions?.[0]?.[key]?.trim().toLowerCase() ===
        requestedProgressionLabel.toLowerCase()
    )
    : null;
  const gradeLevelOptionsArray = configuredGradeLevelOptions
    .slice(0, 3)
    .map((key) =>
      key === matchingConfiguredKey ? requestedProgressionKey : key
    );

  if (
    handoverSelection &&
    requestedProgressionKey &&
    !gradeLevelOptionsArray.includes(requestedProgressionKey)
  ) {
    gradeLevelOptionsArray.push(requestedProgressionKey);
  }

  return (
    <>
      <div className="filter-container">
        <div className="filter-buttons">
          {!showCurrentYearOnly && academicYears.map((year) => (
              <button
                key={year.storage}
                className={`light-blue-button-filter ${selectedFilter === year.display ? "active" : ""}`}
                onClick={() => {
                  setSelectedFilter(year.display);
                }}
              >
                {year.display}
              </button>
            ))}
          {/* Only show "All" button when showCurrentYearOnly is false */}
          {!showCurrentYearOnly && (
            <button
              className={`light-blue-button-filter ${selectedFilter === "all" ? "active" : ""}`}
              onClick={() => {
                setSelectedFilter("all");
              }}
            >
              All
            </button>
          )}
        </div>
        <div className="filter-dropdown">
          <label htmlFor="gradeLevelFilter">Grade Level Filter: </label>
          <select
            id="gradeLevelFilter"
            value={selectedGradeLevel}
            onChange={(e) => {
              setSelectedGradeLevel(e.target.value);
              setSelectedProgressionKey(e.target.value);
            }}
          >
            <option value="all">All</option>
            {gradeLevelOptionsArray.map((key) => (
              <option key={key} value={key}>
                {gradeLevelOptions?.[0]?.[key] ||
                  requestedProgressionLabel ||
                  key}
              </option>
            ))}
          </select>
        </div>
      </div>
    </>
  );
}

export default ClassFilter;
